/**
 * Utility functions for user management
 */

export interface User {
  id?: number;
  user_id?: number;
  email?: string;
  first_name?: string;
  last_name?: string;
  display_name?: string;
  /** Set by Hub login API when email is on sales bypass (not read from Rules admin UI alone). */
  can_sales_bypass?: boolean;
  /** (sales bypass AND facility admin) OR global administrator — Network UI/APIs. */
  can_use_network_facility?: boolean;
  /** Rules/global admin — same cohort that receives llmPromptsBySection. */
  is_global_administrator?: boolean;
  /** Executive email-list cohort. */
  is_executive?: boolean;
  [key: string]: unknown;
}

/**
 * Get the current user ID from localStorage
 * @returns The user ID as a string, or "0" if not found
 */
export const getCurrentUserId = (): string => {
  try {
    const userRaw = localStorage.getItem("user");
    if (userRaw) {
      const user: User = JSON.parse(userRaw);
      return String(user?.id ?? user?.user_id ?? "0");
    }
  } catch (error) {
    console.error("Error parsing user data:", error);
  }
  return "0";
};

/**
 * Get the current user object from localStorage
 * @returns The user object or null if not found
 */
export const getCurrentUser = (): User | null => {
  try {
    const userRaw = localStorage.getItem("user");
    if (userRaw) {
      return JSON.parse(userRaw) as User;
    }
  } catch (error) {
    console.error("Error parsing user data:", error);
  }
  return null;
};

/**
 * Phone from the logged-in user record (profile), if present.
 * Accepts both `phone_number` and legacy `phone` keys.
 */
export const getCurrentUserPhone = (): string => {
  const user = getCurrentUser();
  if (!user) return "";
  const raw = user.phone_number ?? user.phone;
  if (raw == null) return "";
  const trimmed = String(raw).trim();
  return trimmed;
};

/**
 * Historic penalties (enforcement search) — any authenticated Hub user with a valid user id.
 */
export function userCanAccessHistoricPenalties(): boolean {
  const u = getCurrentUser();
  if (!u) return false;
  const rawId = u.id ?? u.user_id;
  const n = rawId != null ? Number(rawId) : 0;
  return !Number.isNaN(n) && n > 0;
}

/** True when the Hub user is on the global administrators list (`is_global_administrator`). */
export function isGlobalAdministrator(): boolean {
  return getCurrentUser()?.is_global_administrator === true;
}

/** Playwright / coverage escape hatch for MultiFacilityStatus route. */
export const MULTI_FACILITY_STATUS_FORCE_KEY = "__ucForceMultiFacilityStatus";

/**
 * Window flag companion to {@link MULTI_FACILITY_STATUS_FORCE_KEY}.
 * Survives `localStorage.clear()` (auth logoutLocal) so the status route
 * is not lost during boot races (PW 26323/26333).
 */
export const MULTI_FACILITY_STATUS_FORCE_WINDOW =
  "__ucForceMultiFacilityStatus";

/**
 * Whether `/dashboard/facilities/status` may render.
 * Global admins always; Playwright sets localStorage / window force flags
 * when CI JWT briefly lacks is_global_administrator during boot.
 */
export function canAccessMultiFacilityStatusPage(): boolean {
  try {
    if (
      typeof window !== "undefined" &&
      (window as unknown as Record<string, unknown>)[
        MULTI_FACILITY_STATUS_FORCE_WINDOW
      ] === true
    ) {
      return true;
    }
  } catch {
    /* ignore */
  }
  try {
    if (localStorage.getItem(MULTI_FACILITY_STATUS_FORCE_KEY) === "1") {
      return true;
    }
  } catch {
    /* ignore */
  }
  return isGlobalAdministrator();
}

/** True when the Hub login API marked the user on the sales bypass list. */
export function userIsSalesUser(): boolean {
  return getCurrentUser()?.can_sales_bypass === true;
}

/** True when (sales bypass AND facility admin) OR global admin — Network UI/APIs. */
export function userCanUseNetworkFacility(): boolean {
  return getCurrentUser()?.can_use_network_facility === true;
}

/**
 * True when the current user can preview verticals other than their own via the
 * left-nav switcher. This is limited to executives and global administrators;
 * sales-bypass alone must not expose the control.
 */
export function canPreviewOtherVerticals(): boolean {
  const u = getCurrentUser();
  if (!u) return false;
  return u.is_executive === true || u.is_global_administrator === true;
}

/**
 * True when Facility Profile (i) tooltips should show the full multi-section
 * help text (When ON / OFF, regulations, calendar items). Sales bypass,
 * executive, and global administrator users qualify.
 */
export function userCanSeeFullProfileHelpText(): boolean {
  const u = getCurrentUser();
  if (!u) return false;
  return (
    u.can_sales_bypass === true ||
    u.is_executive === true ||
    u.is_global_administrator === true
  );
}

/**
 * Profile informational help text for the current viewer. Privileged users get
 * the full string; everyone else gets only the initial paragraph (text before
 * the first blank line), so operational sections stay hidden.
 */
export function profileHelpTextForViewer(text: string): string {
  if (userCanSeeFullProfileHelpText()) return text;
  const trimmed = text.trim();
  if (!trimmed) return text;
  const paragraph = trimmed.split(/\n\n/)[0] ?? trimmed;
  return paragraph.trimEnd();
}

/**
 * User type label for UI. Backend may still send "Internal User"; product name is Standard User.
 */
export function formatUserTypeDisplayName(
  name: string | null | undefined,
): string {
  if (name == null || name === "") return "";
  const trimmed = name.trim();
  if (trimmed.toLowerCase() === "internal user") return "Standard User";
  return trimmed;
}

/** Standard (non-external) Hub user type — legacy API name or new name. */
export function isStandardHubUserTypeName(
  name: string | null | undefined,
): boolean {
  const n = (name || "").trim().toLowerCase();
  return n === "internal user" || n === "standard user";
}
